<template>
  <div class="app-text-field">
    <div v-if="label" class="field-label">
      <v-icon v-if="labelIcon" size="18">{{ labelIcon }}</v-icon>
      <span>{{ label }}</span>
    </div>
    <v-text-field v-model="model" autocomplete="off" :class="fieldClass" :variant="variant" :flat="flat" :hide-details="hideDetails"
      v-bind="fieldAttrs" />
  </div>
</template>

<script setup lang="ts">
import { computed, useAttrs } from "vue";

defineOptions({
  inheritAttrs: false,
});

const model = defineModel();

const props = withDefaults(
  defineProps<{
    variant?: "solo" | "filled" | "outlined" | "plain" | "solo-filled" | "solo-inverted" | "underlined";
    flat?: boolean;
    hideDetails?: boolean | "auto";
    label?: string;
    labelIcon?: string;
  }>(),
  {
    variant: "solo",
    flat: true,
    hideDetails: "auto",
    label: "",
    labelIcon: "",
  },
);

const attrs = useAttrs();
const fieldClass = computed(() => ["app-text-field__input", attrs.class]);

const fieldAttrs = computed(() => {
  const { class: _class, ...rest } = attrs;
  return rest;
});
</script>

<style scoped>
.field-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--color-subtitle);
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 6px;
  padding: 0 10px;
}

.app-text-field__input {
  --v-input-control-height: 48px;
  --v-input-padding-top: 0px;
  --v-field-padding-top: 0px;
  --v-field-padding-bottom: 0px;
  --v-field-input-padding-top: 0px;
  --v-field-input-padding-bottom: 0px;
  --v-field-padding-start: 12px;
  --v-field-padding-end: 12px;
}

.app-text-field__input :deep(.v-field) {
  border: 1px solid #eaeaea;
}

.app-text-field__input :deep(.v-field__input) {
  font-size: 0.875rem;
}

.app-text-field__input :deep(.v-input--density-default .v-field--variant-outlined),
.app-text-field__input :deep(.v-input--density-default .v-field--single-line),
.app-text-field__input :deep(.v-input--density-default .v-field--no-label) {
  --v-field-padding-bottom: 0px !important;
}

.app-text-field__input :deep(.v-input--density-default .v-field--variant-solo),
.app-text-field__input :deep(.v-input--density-default .v-field--variant-solo-inverted),
.app-text-field__input :deep(.v-input--density-default .v-field--variant-solo-filled),
.app-text-field__input :deep(.v-input--density-default .v-field--variant-filled) {
  --v-input-control-height: 48px;
  --v-field-padding-bottom: 0px !important;
}

/* Force error message color to red to match API validation styling */
.app-text-field__input :deep(.v-messages__message) {
  color: #dc2626 !important;
  font-size: 13px;
}
</style>
